// src/components/ProtectedRoute.jsx
import React from 'react';
import { useAuth } from '../Context/AuthContext';
import { useAuthAdmin } from '../Context/useAuthAdmin';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useUserAccess } from '../hooks/queries/useUserData';
import { useAccessScope } from '../hooks/queries/useOrgGroups';
import { withRedirectParam } from '../lib/authRedirect';

export default function ProtectedRoute({
  children,
  requireAdmin = false, // admin or super_admin
  requireCreator = false, // creator only (not admin/super_admin)
  requireAdminOrCreator = false, // NOTE: despite the name this also allows trainer — admin, creator, or trainer
  requireAdminCreatorOnly = false, // admin, creator, or super_admin (excludes trainer, unlike requireAdminOrCreator above)
  requireSuperAdmin = false,
  requireSameOrg = false, // admin managing their own org (see block below); super_admin bypasses
  requireFeature = null,
  requireLearnerOrTrainer = false,
  requireParentDashboardAccess = false,
  requirePlan = null,
  unauthorizedTitle = null,
  unauthorizedMessage = null,
  unauthorizedActions = null,
}) {
  const { user } = useAuth();
  const { isSuperAdmin, isAdmin, isCreator, isLearner, isTrainer, isParent, loading: authLoading } = useAuthAdmin();
  const { data: userAccessData } = useUserAccess(user?.id);
  const accessScopeQuery = useAccessScope(!!user);
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading while auth is being determined. Runs before any
  // role-based gate below so `isSuperAdmin()`/`isAdmin()` etc. (which
  // default to false while auth is resolving) can't cause a false
  // "Unauthorized" flash for a user who is actually authorized once
  // loading finishes.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
              <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading</h3>
            <p className="text-gray-600">Please wait while we verify your access...</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is authenticated. Redirect (rather than requiring a click)
  // so deep links restore automatically after login, carrying the intended
  // destination via both the `redirect` query param and router state. Runs
  // before the role-based gates below so an unauthenticated visitor is sent
  // to /login instead of seeing a same-org/super-admin "Unauthorized" card.
  if (!user) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={withRedirectParam('/login', returnTo)}
        replace
        state={{ from: returnTo }}
      />
    );
  }

  if (requireSuperAdmin && !isSuperAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Super Admin Only</h3>
            <p className="text-gray-600">You need super admin privileges to access this page.</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg">Return to Dashboard</button>
        </div>
      </div>
    );
  }

  // Check organization-based access for own-org management pages (e.g.
  // /admin/org-branding). "Same org" here means: the actor manages their
  // own organization, not an arbitrary one — there is no target org in the
  // URL to compare against. Gate to admin (matches the
  // organizations_update_admins RLS policy, which only grants admin/
  // super_admin write access; creator was previously allowed through this
  // client-side check but every write would silently fail RLS) and require
  // the actor to actually have an organization assigned via useUserAccess,
  // since a user with no org (e.g. "Individual") has nothing to manage.
  // super_admin always bypasses.
  if (requireSameOrg && !isSuperAdmin()) {
    const actorOrganization = userAccessData?.organization || null;
    const hasOwnOrganization = !!actorOrganization && actorOrganization !== 'Individual';
    if (!isAdmin() || !hasOwnOrganization) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
            <div className="mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.02 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Unauthorized Access</h3>
              <p className="text-gray-600">You need admin privileges for an organization to access this page.</p>
            </div>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      );
    }
  }

  // Check role-based permissions
  if (requireAdmin && !isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Admin Access Required</h3>
            <p className="text-gray-600">You need admin privileges to access this page.</p>
          </div>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (requireCreator && !isCreator()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Creator Access Required</h3>
            <p className="text-gray-600">You need creator privileges to access this page.</p>
          </div>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (requireAdminCreatorOnly && !(isAdmin() || isCreator() || isSuperAdmin())) {
    if (unauthorizedTitle || unauthorizedMessage || (unauthorizedActions && unauthorizedActions.length)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
            <div className="mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {unauthorizedTitle || 'Unauthorized Access'}
              </h3>
              <p className="text-gray-600">
                {unauthorizedMessage || 'You need admin or creator privileges to access this page.'}
              </p>
            </div>
            <div className="space-y-2">
              {(unauthorizedActions || []).map((action) => (
                <button
                  key={action.to}
                  onClick={() => navigate(action.to)}
                  className={`w-full font-semibold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg ${
                    action.variant === 'primary'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-800'
                      : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800'
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Unauthorized Access</h3>
            <p className="text-gray-600">You need admin or creator privileges to access this page.</p>
          </div>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (requireAdminOrCreator && !(isAdmin() || isCreator() || isTrainer())) {
    if (unauthorizedTitle || unauthorizedMessage || (unauthorizedActions && unauthorizedActions.length)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
            <div className="mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {unauthorizedTitle || 'Unauthorized Access'}
              </h3>
              <p className="text-gray-600">
                {unauthorizedMessage || 'You need admin, creator, or trainer privileges to access this page.'}
              </p>
            </div>
            <div className="space-y-2">
              {(unauthorizedActions || []).map((action) => (
                <button
                  key={action.to}
                  onClick={() => navigate(action.to)}
                  className={`w-full font-semibold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg ${
                    action.variant === 'primary'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-800'
                      : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800'
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Unauthorized Access</h3>
            <p className="text-gray-600">You need admin, creator, or trainer privileges to access this page.</p>
          </div>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Check for learner or trainer access (for analytics, course viewing, etc.)
  if (requireLearnerOrTrainer && !(isLearner() || isTrainer() || isAdmin() || isCreator() || isSuperAdmin())) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h3>
            <p className="text-gray-600">You need learner or trainer access to view this page.</p>
          </div>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (requireParentDashboardAccess && !(isParent() || isAdmin() || isSuperAdmin())) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h3>
            <p className="text-gray-600">You need parent, admin, or super admin access to view this page.</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (requirePlan) {
    const allowedPlans = Array.isArray(requirePlan) ? requirePlan : [requirePlan];
    const usagePlan = String(userAccessData?.usage_plan || 'free').toLowerCase();
    if (!allowedPlans.map((p) => String(p).toLowerCase()).includes(usagePlan) && !isSuperAdmin()) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
            <div className="mb-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 1.343-3 3v2H8a1 1 0 000 2h8a1 1 0 100-2h-1v-2c0-1.657-1.343-3-3-3zM7 11a5 5 0 1110 0v2h.5a2.5 2.5 0 010 5h-11a2.5 2.5 0 010-5H7v-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Plan Upgrade Required</h3>
              <p className="text-gray-600">Your current plan does not include access to this page.</p>
            </div>
            <button onClick={() => navigate('/plans')} className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg">View Plans</button>
          </div>
        </div>
      );
    }
  }

  // Organization feature check (simple client-side fetch)
  if (requireFeature) {
    if (!isSuperAdmin() && accessScopeQuery.isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading</h3>
              <p className="text-gray-600">Checking feature access for your organization.</p>
            </div>
          </div>
        </div>
      );
    }

    try {
      if (!isSuperAdmin()) {
        const required = Array.isArray(requireFeature) ? requireFeature : [requireFeature];
        const features = accessScopeQuery.data?.effectiveFeatures;
        const ok = required.every((key) => features?.[key] !== false);
        if (!ok) {
          return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center border border-gray-200">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.02 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Feature Not Enabled</h3>
                  <p className="text-gray-600">Your organization or group access does not include this feature.</p>
                </div>
                <button onClick={() => navigate('/dashboard')} className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-gray-700 hover:to-gray-800 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg">Return to Dashboard</button>
              </div>
            </div>
          );
        }
      }
    } catch (featureCheckError) {
      console.warn('[ProtectedRoute] Feature gate evaluation failed', featureCheckError);
    }
  }

  return children;
}
